import moment from "moment";
import { mapState } from "vuex";

import { countByKeys } from "@/utils/countByKeys";
import { toTranslationKey } from "@/utils/stringFormat";
import { PremiumServiceEventTypeEnum, PremiumServiceEventTypes, PremiumServiceProductTypeEnum, PremiumServiceRequestTypeEnum } from "@/enums/PremiumServiceEventTypes";
import { ServiceStatus } from "@/enums/ServiceStatus";

export default {
  data() {
    return {
      eventTypes: Object.values(PremiumServiceEventTypeEnum),
      requestTypes: Object.values(PremiumServiceRequestTypeEnum),
      productTypes: Object.values(PremiumServiceProductTypeEnum),
      loading: true,
      headers: [],
      jsonFields: {
        Service: "name",
        Type: "serviceItemType",
        Price: "cost",
        Provider: "providerName",
        Dimension: "category"
      }
    };
  },
  async created() {
    try {
      this.headers = [
        { text: this.$t('GeneralTranslations.Service'), align: "start", value: "name" },
        { text: this.$t('GeneralTranslations.Type'), value: "serviceItemType" },
        { text: this.$t('GeneralTranslations.Price'), value: "cost" },
        { text: this.$t('GeneralTranslations.Provider'), value: "providerName" },
        { text: this.$t('GeneralTranslations.Dimension'), value: "category" },
      ]
      await this.$store.dispatch("getAdditionalServices");
      await this.$store.dispatch("getServiceRequests");
      await this.$store.dispatch("getFacility");
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  },
  computed: {
    ...mapState({
      facility: (state) => (state as any).facilityModule.selectedFacility,
      residents: state => (state as any).residentModule.residentsShortData,
      requests: state => (state as any).additionalServicesModule.serviceRequests
    }),
    services() {
      return this.$store.state.additionalServicesModule.additionalServices.map((s: any) => {

        const service = s

        if (this.eventTypes.includes(s.type)) {
          service.serviceItemType = PremiumServiceEventTypes.Event
        }
        else if (this.requestTypes.includes(s.type)) {
          service.serviceItemType = PremiumServiceEventTypes.Request
        }
        else if (this.productTypes.includes(s.type)) {
          service.serviceItemType = PremiumServiceEventTypes.Product
        }

        return service
      })
    },
    conciergeServices() {
      const requests = this.$store.state.additionalServicesModule.serviceRequests
        .filter(x => [
          PremiumServiceEventTypes.AppAccess,
          PremiumServiceEventTypes.ByAppointment,
          PremiumServiceEventTypes.Request,
          PremiumServiceEventTypes.Event,
          PremiumServiceEventTypes.EventBased,
          PremiumServiceEventTypes.ConciergeEvent,
          PremiumServiceEventTypes.Gift,
          PremiumServiceEventTypes.Product
        ].includes(x.serviceItemType));
      return requests
    },
    conciergeEvents() {
      return this.services.filter((x: any) => x.serviceItemType == PremiumServiceEventTypes.Event)
    },
    conciergeRequests() {
      return this.services.filter((x: any) => x.serviceItemType == PremiumServiceEventTypes.Request)
    },
    conciergeProducts() {
      return this.services.filter((x: any) => x.serviceItemType == PremiumServiceEventTypes.Product)
    },
    requestsWithServices() {
      return this.requests.map(request => {
        const service = this.services.find(
          service => service.id === request.serviceId
        );

        return {
          request,
          service
        };
      });
    },
    premiumServiceReport() {
      return this.services;
    },
    residentsWithMostsServices() {
      const residentsWithServices = this.residents.map(resident => {
        const requests = this.conciergeServices.filter(x => x.residentId === resident.id && [ServiceStatus.Completed, ServiceStatus.Approved, ServiceStatus.AwaitingApproval].includes(x.status))

        const item = {
          id: resident.id,
          name: resident?.name || resident?.pname,
          conciergeRequestsCount: requests.length,
          imageUrl: resident?.imageUrl,
        }

        return item;
      }).sort((left, right) => right.conciergeRequestsCount - left.conciergeRequestsCount).slice(0, 6);

      return residentsWithServices;
    },
    servicesByProvider() {
      return countByKeys(this.services, "providerName");
    },
    servicesByCategory() {
      return countByKeys(this.services, "category");
    },
    servicesByChargeType() {
      return countByKeys(this.services, "serviceType");
    },
    serviceRequestWithBookedBy() {
      return this.requests.map(request => {
        let bookedBy;

        if (request.bookByFamilyName) {
          bookedBy = this.$t('GeneralTranslations.Family');
        } else if (request.bookByStaffName) {
          bookedBy = this.$t('GeneralTranslations.Staff');
        } else {
          bookedBy = this.$t('GeneralTranslations.Resident');
        }

        return {
          ...request,
          bookedBy
        };
      });
    },
    servicesByBookedBy() {
      return countByKeys(this.serviceRequestWithBookedBy, "bookedBy");
    },
    servicesByDays() {
      const sortedRequest = this.requests.sort((left, right) =>
        moment.utc(left.createdDateTime).diff(moment.utc(right.createdDateTime))
      );

      const formattedRequests = sortedRequest.map(request => ({
        ...request,
        createdDateTime: moment(request.createdDateTime).format("DD/MM")
      }));
      return countByKeys(formattedRequests, "createdDateTime");
    },
    facilityCurrency() {
      return this.$store.getters.facilityCurrency;
    }
  },
  methods: {
    routeToLConciergeServiceReportByResidents() {
      this.$router.push("/reports/premium-service-reports/residents");
    },
    convertToTranslationKey(text: string) {
      return toTranslationKey(text)
    }
  }
};
